import { CommonModule } from '@angular/common'
import { HttpParams } from '@angular/common/http'
import { Component, OnInit, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { RouterLink } from '@angular/router'
import { WjFlexGrid, WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { NgbNav, NgbNavChangeEvent, NgbNavContent, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavOutlet } from '@ng-bootstrap/ng-bootstrap'

import { FeatureType, Grid, ModalBackDrop, ModalSize, PartsFluidsItem, ViewportService } from '../../../shared'
import { BaseModal } from '../../../shared/modals/base/base-modal'
import { ActionToolbarCount } from '../../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { CreatePoFromPartsComponent } from './create-po-from-parts.component'
import { WorkOrderHistoryComponent } from '../../work-order-queue/work-order-history.component'
import {
    WorkOrderHistoryEquipment,
    WorkOrderHistoryOpenEvent,
    WorkOrderNotesItemGroup,
    WorkOrderNotesResponse,
    WorkOrderTimeCardNote,
} from '../../work-order-queue/work-order-history.model'

export interface WorkOrderPartsFluidsRow extends PartsFluidsItem {
    selected: boolean
    fromCatalogSearch: boolean
}

export interface WorkOrderLineItem {
    id?: string
    workOrderId?: string
    woitem: number
    equipment?: string
    description?: string
    statusCode?: string
    repairType?: string
    costCode?: string
    mechanicHrRef?: number
    priority?: string
    dateCreated?: string
    dateDue?: string
    dateSched?: string
    notes?: string
}

export interface WorkOrderPurchaseOrderSummary {
    id: string
    poNumber: string
    status: string
    vendorName: string
    lineCount: number
    total?: number
    receiptCount: number
    createdOn?: string
}

@Component({
    templateUrl: './work-order-details.component.html',
    styleUrls: ['./work-order-details.component.css'],
    standalone: true,
    host: {
        '[class.wo-details-mobile]': 'isMobileLayout()',
    },
    imports: [
        CommonModule,
        FormsModule,
        RouterLink,
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        NgbNavContent,
        NgbNavOutlet,
        WjGridModule,
        WjGridFilterModule,
        ActionToolbarCount,
        ActionToolbarSearchComponent,
        WorkOrderHistoryComponent,
    ],
})
export class WorkOrderDetailsComponent extends BaseModal implements OnInit {
    private viewport = inject(ViewportService)
    readonly isMobileLayout = this.viewport.isMobile

    active = 'workOrder'
    equipmentActive = 'details'
    workOrder!: string
    equipmentId!: string
    source?: string
    workOrderId?: string
    job?: string

    itemsGrid: Grid<WorkOrderLineItem>
    metersGrid: Grid<any>
    partsGrid: Grid<WorkOrderPartsFluidsRow>
    warrantyGrid: Grid<any>

    catalogSearch = ''
    purchaseOrders: WorkOrderPurchaseOrderSummary[] = []
    isSearchingCatalog = false
    isLoadingItems = true

    notes: WorkOrderNotesResponse | null = null
    notesFilter = ''
    showAllNotes = false
    notesLoaded = false
    isLoadingNotes = false
    equipmentSummary: WorkOrderHistoryEquipment | null = null
    readonly notesCap = 200

    get canViewPurchaseOrders(): boolean {
        return this.services.authentication.hasFeature([FeatureType.PurchasingPurchaseOrdersView])
    }

    get canCreatePurchaseOrders(): boolean {
        return this.services.authentication.hasFeature([FeatureType.PurchasingPurchaseOrdersEdit])
    }

    get selectedParts(): WorkOrderPartsFluidsRow[] {
        return (this.partsGrid.items ?? []).filter(x => x.selected)
    }

    get filteredNoteGroups(): WorkOrderNotesItemGroup[] {
        const items = this.notes?.items ?? []
        const needle = this.notesFilter.trim().toLowerCase()
        if (!needle) return items

        return items
            .map(group => {
                const groupText = [group.description, group.itemNotes, group.statusCode].join(' ').toLowerCase()
                const matchingMemos = (group.timeCardNotes ?? []).filter(note => this.memoMatches(note, needle))
                if (groupText.includes(needle))
                    return matchingMemos.length > 0 ? { ...group, timeCardNotes: matchingMemos } : group
                if (matchingMemos.length === 0) return null
                return { ...group, timeCardNotes: matchingMemos }
            })
            .filter((group): group is WorkOrderNotesItemGroup => group != null)
    }

    get totalFilteredMemoCount(): number {
        return this.filteredNoteGroups.reduce((sum, group) => sum + (group.timeCardNotes?.length ?? 0), 0)
    }

    get visibleNoteGroups(): WorkOrderNotesItemGroup[] {
        const filtered = this.filteredNoteGroups
        if (this.showAllNotes || this.totalFilteredMemoCount <= this.notesCap) return filtered

        const ranked = filtered
            .flatMap(group => (group.timeCardNotes ?? []).map(note => ({ group, note, time: Date.parse(note.postDate) || 0 })))
            .sort((a, b) => b.time - a.time)
            .slice(0, this.notesCap)

        const allowed = new Set<WorkOrderTimeCardNote>(ranked.map(row => row.note))
        return filtered
            .map(group => ({
                ...group,
                timeCardNotes: (group.timeCardNotes ?? []).filter(note => allowed.has(note)),
            }))
            .filter(group => (group.timeCardNotes?.length ?? 0) > 0 || !!group.itemNotes)
    }

    get notesAreCapped(): boolean {
        return !this.showAllNotes && this.totalFilteredMemoCount > this.notesCap
    }

    constructor() {
        super()

        this.itemsGrid = new Grid<WorkOrderLineItem>(this.services, 'woitem')
            .wrapColumnHeaders()
            .isReadOnly(true)
            .doInitialSelection(false)
            .filterBy(item => [
                item.woitem,
                item.description,
                item.statusCode,
                item.repairType,
                item.costCode,
                item.priority,
                item.notes,
                item.equipment,
            ])
        this.metersGrid = new Grid<any>(this.services).wrapColumnHeaders().isReadOnly(true).doInitialSelection(false)
        this.partsGrid = new Grid<WorkOrderPartsFluidsRow>(this.services)
            .wrapColumnHeaders()
            .isReadOnly(false)
            .doInitialSelection(false)
            .filterBy(item => [
                item.source,
                item.category,
                item.subCategory,
                item.itemLabel,
                item.itemValue,
                item.suggestedVendor,
                item.details,
                item.notes,
                item.equipment,
            ])
        this.warrantyGrid = new Grid<any>(this.services)
            .wrapColumnHeaders()
            .isReadOnly(true)
            .autoGenerateColumns()
            .doInitialSelection(false)
    }

    initializeItemsGrid(flexGrid: WjFlexGrid) {
        flexGrid.rows.defaultSize = 48
        this.itemsGrid.initialize(flexGrid)
        flexGrid.rows.defaultSize = 48
        for (let i = 0; i < flexGrid.rows.length; i++) {
            flexGrid.rows[i].height = 48
        }
    }

    ngOnInit(): void {
        this.refresh()
    }

    refresh(): void {
        if (this.workOrder && this.equipmentId) {
            const params = new HttpParams().set('workOrder', this.workOrder).set('equipmentId', this.equipmentId)
            this.services.data.load(true, true, this.services.data.get('/api/VpData/QueryWorkOrderDetails', params)).subscribe({
                next: result => {
                    const data = result[0] as any
                    this.metersGrid.items = data.meterRecords || []
                    this.warrantyGrid.items = data.warrantyRecords || []
                },
            })
        }

        this.loadWorkOrderItems()
        this.loadNotes()
        this.loadPartsAndFluids()
        this.loadPurchaseOrders()
        this.loadEquipmentSummary()
    }

    onNavChange(event: NgbNavChangeEvent) {
        if (event.nextId !== 'equipment') return
        this.refreshEquipmentGrids()
    }

    onEquipmentNavChange() {
        this.refreshEquipmentGrids()
    }

    private refreshEquipmentGrids() {
        setTimeout(() => {
            this.metersGrid.flexGrid?.refresh()
            this.warrantyGrid.flexGrid?.refresh()
            this.partsGrid.flexGrid?.refresh()
            this.services.utility.raiseLayoutRequiresUpdate()
        }, 50)
    }

    openFromHistory(row: WorkOrderHistoryOpenEvent) {
        if (row.workOrderNumber === this.workOrder && (row.webAppsId ?? '') === (this.workOrderId ?? '')) return

        this.workOrder = row.workOrderNumber
        this.workOrderId = row.webAppsId
        this.equipmentId = row.equipment || this.equipmentId
        this.source = row.source
        this.job = row.job ?? this.job
        this.notes = null
        this.notesLoaded = false
        this.showAllNotes = false
        this.notesFilter = ''
        this.refresh()
    }

    loadNotes(): void {
        if (!this.workOrderId && !this.workOrder) {
            this.notes = { items: [], totalHours: 0, timeCardLookupSkipped: false }
            this.notesLoaded = true
            this.isLoadingNotes = false
            return
        }

        this.isLoadingNotes = true
        let params = new HttpParams()
        if (this.equipmentId) params = params.set('equipment', this.equipmentId)
        if (this.workOrder) params = params.set('workOrder', this.workOrder)
        if (this.workOrderId) params = params.set('workOrderId', this.workOrderId)

        this.services.data
            .load(true, true, this.services.data.get<WorkOrderNotesResponse>('/api/EquipmentWorkOrder/notes', params))
            .subscribe({
                next: result => {
                    this.notes = (result[0] ?? { items: [], totalHours: 0, timeCardLookupSkipped: false }) as WorkOrderNotesResponse
                    this.notes.items = this.notes.items ?? []
                    this.notesLoaded = true
                    this.isLoadingNotes = false
                },
                error: () => {
                    this.notes = { items: [], totalHours: 0, timeCardLookupSkipped: false }
                    this.notesLoaded = true
                    this.isLoadingNotes = false
                },
            })
    }

    loadEquipmentSummary(): void {
        const code = (this.equipmentId ?? '').trim()
        if (!code) {
            this.equipmentSummary = null
            return
        }

        const params = new HttpParams().set('equipment', code)
        this.services.data.get<WorkOrderHistoryEquipment>('/api/EquipmentWorkOrder/equipment-summary', params).subscribe({
            next: result => (this.equipmentSummary = result ?? null),
            error: () => (this.equipmentSummary = null),
        })
    }

    loadWorkOrderItems(): void {
        if (!this.workOrderId && !this.workOrder) {
            this.itemsGrid.items = []
            this.isLoadingItems = false
            return
        }

        this.isLoadingItems = true
        let params = new HttpParams()
        if (this.equipmentId) params = params.set('equipment', this.equipmentId)
        if (this.workOrder) params = params.set('workOrder', this.workOrder)
        if (this.workOrderId) params = params.set('workOrderId', this.workOrderId)

        this.services.data
            .load(true, true, this.services.data.get<WorkOrderLineItem[]>('/api/EquipmentWorkOrder/items', params))
            .subscribe({
                next: result => {
                    this.itemsGrid.items = (result[0] ?? []) as WorkOrderLineItem[]
                    this.isLoadingItems = false
                },
                error: () => {
                    this.itemsGrid.items = []
                    this.isLoadingItems = false
                },
            })
    }

    loadPartsAndFluids(): void {
        if (!this.equipmentId) {
            this.partsGrid.items = []
            return
        }

        const params = new HttpParams().set('equipment', this.equipmentId)
        this.services.data
            .load(true, true, this.services.data.get<PartsFluidsItem[]>('/api/EquipmentWorkOrder/PartsAndFluids', params))
            .subscribe({
                next: result => {
                    const items = (result[0] ?? []) as PartsFluidsItem[]
                    this.partsGrid.items = items.map(item => this.toRow(item, false))
                },
            })
    }

    searchCatalog(): void {
        const term = this.catalogSearch.trim()
        if (!term) {
            this.partsGrid.items = (this.partsGrid.items ?? []).filter(x => !x.fromCatalogSearch)
            return
        }
        if (term.length < 2) {
            this.services.notification.error('Enter at least 2 characters to search the catalog.')
            return
        }

        this.isSearchingCatalog = true
        const params = new HttpParams().set('search', term)
        this.services.data.get<PartsFluidsItem[]>('/api/EquipmentWorkOrder/PartsAndFluidsSearch', params).subscribe({
            next: items => {
                this.isSearchingCatalog = false
                const existingKeys = new Set((this.partsGrid.items ?? []).map(x => this.rowKey(x)))
                const additions = (items ?? [])
                    .filter(item => !existingKeys.has(this.rowKey(item)))
                    .map(item => this.toRow(item, true))
                this.partsGrid.items = [...(this.partsGrid.items ?? []), ...additions]
                if (additions.length === 0)
                    this.services.notification.info('No additional catalog items matched that search.')
            },
            error: () => {
                this.isSearchingCatalog = false
                this.services.notification.error('Failed to search parts and fluids catalog.')
            },
        })
    }

    toggleAllSelected(event: Event): void {
        const checkbox = event.target as HTMLInputElement
        this.partsGrid.view.items.forEach(x => (x.selected = checkbox.checked))
        this.partsGrid.refresh()
    }

    togglePartSelected(item: WorkOrderPartsFluidsRow, event: Event): void {
        item.selected = (event.target as HTMLInputElement).checked
    }

    onPartsEdited(): void {
        // Kept for any remaining grid edits so the Create PO button count updates.
    }

    openCreatePurchaseOrder(): void {
        const selected = this.selectedParts
        if (selected.length === 0) {
            this.services.notification.error('Select at least one part or fluid.')
            return
        }

        const ref = this.services.modals.open(CreatePoFromPartsComponent, ModalSize.extraLarge, ModalBackDrop.darkAndActive)
        const instance = ref.componentInstance as CreatePoFromPartsComponent
        instance.items = selected
        instance.workOrderItems = [...(this.itemsGrid.items ?? [])]
        instance.equipment = this.equipmentId
        instance.workOrder = this.workOrder
        instance.workOrderId = this.workOrderId
        instance.job = this.job

        ref.result.then(
            () => this.loadPurchaseOrders(),
            () => {},
        )
    }

    loadPurchaseOrders(): void {
        if (!this.canViewPurchaseOrders) {
            this.purchaseOrders = []
            return
        }
        if (!this.workOrderId && !this.workOrder) {
            this.purchaseOrders = []
            return
        }

        let params = new HttpParams()
        if (this.equipmentId) params = params.set('equipment', this.equipmentId)
        if (this.workOrder) params = params.set('workOrder', this.workOrder)
        if (this.workOrderId) params = params.set('workOrderId', this.workOrderId)

        this.services.data.get<WorkOrderPurchaseOrderSummary[]>('/api/PurchasingOrder/GetByWorkOrder', params).subscribe({
            next: result => (this.purchaseOrders = result || []),
            error: () => (this.purchaseOrders = []),
        })
    }

    private toRow(item: PartsFluidsItem, fromCatalogSearch: boolean): WorkOrderPartsFluidsRow {
        return {
            ...item,
            selected: false,
            fromCatalogSearch,
        }
    }

    private rowKey(item: PartsFluidsItem): string {
        return [item.source, item.equipment, item.category, item.subCategory, item.itemLabel, item.itemKey, item.recordId ?? ''].join('|')
    }

    private memoMatches(note: WorkOrderTimeCardNote, needle: string): boolean {
        const text = [note.memo, note.employeeName, note.costCode, String(note.hours)].join(' ').toLowerCase()
        return text.includes(needle)
    }
}
